<template>
  <v-card-text class="text-body-1">
    <p
      v-for="(text, i) in introParagraphs"
      :key="i"
      class="mb-4"
    >
      {{ text }}
    </p>

    <p class="font-weight-medium mt-6">
      {{ introConclusion }}
    </p>
  </v-card-text>

  <!-- Tabla de umbrales -->
  <v-table
    class="mt-4"
    density="comfortable"
    :aria-label="t('umbrales.ariaTabla')"
  >
    <caption class="sr-only">
      Umbrales de clasificación por colores
    </caption>

    <thead>
      <tr>
        <th scope="col">
          {{ t("umbrales.parametro") }}
        </th>
        <th scope="col">
          <span class="tier-name">{{ t("opiniones.tier.excepcional") }}</span>
          <span class="tier-color">{{ t("umbrales.verdeOscuro") }}</span>
        </th>
        <th scope="col">
          <span class="tier-name">{{ t("opiniones.tier.buena") }}</span>
          <span class="tier-color">{{ t("umbrales.verde") }}</span>
        </th>
        <th scope="col">
          <span class="tier-name">{{ t("opiniones.tier.regular") }}</span>
          <span class="tier-color">{{ t("umbrales.amarillo") }}</span>
        </th>
        <th scope="col">
          <span class="tier-name">{{ t("opiniones.tier.mala") }}</span>
          <span class="tier-color">{{ t("umbrales.rojo") }}</span>
        </th>
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="row in tableRows"
        :key="row.label"
      >
        <th scope="row">
          {{ row.label }}
        </th>
        <td>
          <span class="color-cell"><i
            class="svg-icon svg32"
            :class="row.icons.darkGreen"
          />{{ row.darkGreen }}</span>
        </td>
        <td>
          <span class="color-cell"><i
            class="svg-icon svg32"
            :class="row.icons.green"
          />{{ row.green }}</span>
        </td>
        <td>
          <span class="color-cell"><i
            class="svg-icon svg32"
            :class="row.icons.yellow"
          />{{ row.yellow }}</span>
        </td>
        <td>
          <span class="color-cell"><i
            class="svg-icon svg32"
            :class="row.icons.red"
          />{{ row.red }}</span>
        </td>
      </tr>
    </tbody>
  </v-table>

  <v-card-text class="text-body-2 text-medium-emphasis mt-4">
    {{ t("umbrales.footer") }}
  </v-card-text>
</template>

<script setup>
    import { computed } from "vue";
    import { useI18n } from "vue-i18n";
    import { LEGEND_META, COLOR_SCALE } from "@/utils/helpers";

    const colors = COLOR_SCALE.colors;

    const { t } = useI18n();

    /**
     * Construye los textos de cada columna desde los rangos
     */
    function buildThresholds(values, unit, inverted = false) {
    const v = [...values];

    if (inverted) {
        // ping (menor es mejor)
        return {
        darkGreen: `≤ ${v[v.length - 2]} ${unit}`,
        green: `≤ ${v[v.length - 3]} ${unit}`,
        yellow: `≤ ${v[v.length - 4]} ${unit}`,
        red: `> ${v[v.length - 4]} ${unit}`,
        };
    }

    // resto (mayor es mejor)
    return {
        darkGreen: `≥ ${v[v.length - 2]} ${unit}`,
        green: `≥ ${v[v.length - 3]} ${unit}`,
        yellow: `≥ ${v[v.length - 4]} ${unit}`,
        red: `< ${v[v.length - 4]} ${unit}`,
    };
    }

    const ICON_MAP = {
      download: {
        header: "svg-down-header",
        darkGreen: "svg-down-4",
        green: "svg-down-3",
        yellow: "svg-down-2",
        red: "svg-down-1",
      },
      upload: {
        header: "svg-up-header",
        darkGreen: "svg-up-4",
        green: "svg-up-3",
        yellow: "svg-up-2",
        red: "svg-up-1",
      },
      ping: {
        header: "svg-ping-header",
        darkGreen: "svg-ping-4",
        green: "svg-ping-3",
        yellow: "svg-ping-2",
        red: "svg-ping-1",
      },
      signal_mobile: {
        header: "svg-signal-header",
        darkGreen: "svg-mobile-4",
        green: "svg-mobile-3",
        yellow: "svg-mobile-2",
        red: "svg-mobile-1",
      },
      signal_wlan: {
        header: "svg-signal-header",
        darkGreen: "svg-wlan4-4",
        green: "svg-wlan4-3",
        yellow: "svg-wlan4-2",
        red: "svg-wlan4-1",
      },
    };

    const tableRows = computed(() => [
    {
        key: "download",
        inverted: false,
    },
    {
        key: "upload",
        inverted: false,
    },
    {
        key: "ping",
        inverted: true,
    },
    {
        key: "signal_mobile",
        inverted: false,
    },
    {
        key: "signal_wlan",
        inverted: false,
    },
    ].map(({ key, inverted }) => {
    const meta = LEGEND_META[key];
    const iconSet = ICON_MAP[key];

    return {
        label: t(meta.titleKey),
        iconHeader: iconSet.header,
        icons: {
          darkGreen: iconSet.darkGreen,
          green: iconSet.green,
          yellow: iconSet.yellow,
          red: iconSet.red,
        },
        ...buildThresholds(meta.values, meta.subtitle, inverted),
    };
    }));

    const introParagraphs = computed(() => [
        t("umbrales.intro.p1"),
        t("umbrales.intro.p2"),
        t("umbrales.intro.p3"),
        t("umbrales.intro.p4"),
        t("umbrales.intro.p5"),
        t("umbrales.intro.baremo"),
        ]);

    const introConclusion = computed(() =>
        t("umbrales.intro.p6")
    );
</script>

<style scoped>
.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.color-header,
.color-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Cabecera de tabla: nombre del baremo (coherente con el sidebar de
   Opiniones) en grande y el nombre del color como complemento. */
.tier-name {
  display: block;
  font-weight: 700;
  font-size: 0.95em;
  letter-spacing: 0.01em;
}
.tier-color {
  display: block;
  font-weight: 400;
  font-size: 0.78em;
  color: #64748b;
  margin-top: 2px;
}
</style>